import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import styles from './AccountCard.module.css';
import messages from './AccountCard.messages';
import cardMessages from '../accountHistoryTable/AccountHistoryTable.messages';
import ProductType from '../../ui/productType/ProductType';
import { ArrowSVG } from '../../../svgComponents/arrowSVG/index';
import AccountHistoryTable from '../accountHistoryTable/AccountHistoryTable';
import IwMedia from '../../../iwMedia';
import AccountHistoryTableMobile from '../accountHistoryTableMobile/AccountHistoryTableMobile';
import { Button, Icon, Modal } from '@iwoca/orion';
import { connect } from 'react-redux';
import { ACCOUNT_HISTORY_DOWNLOAD } from '../AccountHistory.actions';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import * as selectors from '../AccountHistorySelectors';

const mapStateToProps = (state: RootState, ownProps: AccountCardProps) => ({
    isDownloading: state.accountHistory.downloading,
    hasTransactions: selectors.hasMadeTransactions(state, ownProps.accountId)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    downloadAsPDF: (accountId: string) =>
        dispatch({
            type: ACCOUNT_HISTORY_DOWNLOAD,
            payload: { accountId: accountId }
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type AccountCardProps = {
    accountId: string;
    productType: string;
    balance: number;
    duration: {
        amount: number;
        unit: string;
    };
};

const AccountCard = ({
    accountId,
    productType,
    balance,
    downloadAsPDF,
    isDownloading,
    hasTransactions,
    duration
}: AccountCardProps & PropsFromRedux) => {
    const [areTransactionsOpen, setAreTransactionsOpen] = useState(false);

    const handleModal = () => setAreTransactionsOpen(!areTransactionsOpen);

    const durationInMonths = Math.floor(duration.amount / 30);

    return (
        <>
            <button
                className={styles.accountCardWithHistory}
                onClick={handleModal}
            >
                <span className={styles.accountInfo}>
                    <span className={styles.accountType}>
                        <ProductType
                            productType={productType}
                            durationInMonths={durationInMonths}
                        />
                    </span>

                    <span className={styles.balance}>
                        <span className={styles.balanceLabel}>
                            <FormattedMessage {...messages.balanceLabel} />
                        </span>

                        <span
                            data-testid="outstanding-balance"
                            className={styles.balanceAmount}
                        >
                            <FormattedCurrency value={balance} />
                        </span>
                    </span>
                </span>

                <span className={styles.activeDivider} />

                <span className={styles.accountCTA}>
                    <FormattedMessage {...messages.accountCTA} />
                    <ArrowSVG />
                </span>
            </button>

            <IwMedia from="large">
                <Modal
                    style={{ maxWidth: '1000px' }}
                    isOpen={areTransactionsOpen}
                    onClose={handleModal}
                    headerBar
                    closeIcon
                    title={
                        (
                            <div className={styles.modalTitle}>
                                <FormattedMessage
                                    {...cardMessages.title}
                                    values={{
                                        accountType: (
                                            <ProductType
                                                productType={productType}
                                                durationInMonths={
                                                    durationInMonths
                                                }
                                            />
                                        )
                                    }}
                                />
                                {hasTransactions && (
                                    <Button
                                        variant="secondary-alt"
                                        onClick={() => downloadAsPDF(accountId)}
                                        loading={isDownloading}
                                        className={styles.download}
                                    >
                                        <FormattedMessage
                                            {...cardMessages.download}
                                        />
                                        <Icon icon="download" />
                                    </Button>
                                )}
                            </div>
                        ) as any
                    }
                >
                    <AccountHistoryTable
                        accountId={accountId}
                        productType={productType}
                    />
                </Modal>
            </IwMedia>

            <IwMedia to="large">
                <Modal
                    className={styles.mobileModal}
                    isOpen={areTransactionsOpen}
                    onClose={handleModal}
                    closeIcon
                    title={
                        (
                            <div className={styles.modalTitle}>
                                <FormattedMessage
                                    {...cardMessages.title}
                                    values={{
                                        accountType: (
                                            <ProductType
                                                productType={productType}
                                                durationInMonths={
                                                    durationInMonths
                                                }
                                            />
                                        )
                                    }}
                                />
                                {hasTransactions && (
                                    <Button
                                        variant="secondary-alt"
                                        onClick={() => downloadAsPDF(accountId)}
                                        loading={isDownloading}
                                        className={styles.download}
                                        iconOnly
                                        aria-label="download"
                                        data-testid="download_button"
                                    >
                                        <Icon icon="download" />
                                    </Button>
                                )}
                            </div>
                        ) as any
                    }
                >
                    <AccountHistoryTableMobile
                        accountId={accountId}
                        accountType={productType}
                    />
                </Modal>
            </IwMedia>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(AccountCard);
